import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorLabel, mediaQuery, PrimaryLoadingButton, StyledH3, Subtext } from 'components';
import { CustomerPaymentMethod, PAYMENT_METHOD_GIFTCARD } from 'types/magento/CustomerPaymentMethod';
import QuoteDataShippingMethod from 'mage-swagfaces/quote/QuoteDataShippingMethod';
import { useDispatch, useSelector } from 'react-redux';
import { TrackingProp } from 'react-tracking';
import styled from 'styled-components';
import CartItemList from 'components/CartItemList';
import Show from 'components/Functional/Show';
import CheckoutLayout from 'components/Layouts/CheckoutLayout';
import { PageHeader } from 'components/PageElements';
import CheckoutReviewTiles from 'components/CheckoutReviewTiles';
import { SEO } from 'components/SEO';
import { ShippingMethodOptions } from 'components/Shipping';
import { isNotEmpty } from 'helpers/arrays';
import {
  buildPlaceOrderRequestPayload,
  findCustomerPaymentMethodInCartPaymentMethods,
  hasBraintreePayment as _hasBraintreePayment,
  paypal
} from 'helpers/billing';
import { useWindowSize } from 'hooks/useWindowSize';
import { getGrandTotalFromTotalSegments } from 'store/cart/magentoSelectors';
import {
  cartLoadingStateSelector,
  cartPaymentMethodsSelector,
  isAfterpayAppliedToCartSelector,
  isPaypalEnabledSelector,
  isVirtualCartSelector,
  placeOrderLoadingStateSelector,
  shippingAddressLoadingStateSelector,
  shippingAddressSelector,
  shippingMethodSelector,
  shippingRatesSelector,
  storeCreditLoadingStateSelector,
  totalSegmentsSelector
} from 'store/cart/selectors';
import { isFetching, isSuccess } from 'store/constants';
import {
  customerEmailSelector,
  customerLoadingStateSelector,
  customerSelector,
  customerShippingAddressLoadingStateSelector
} from 'store/customer/selectors';
import { openPaymentMethodSelectionModal, openShippingAddressSelectionModal } from 'store/modal/actions';
import PaypalButton from 'components/Billing/BillingForm/PaypalButton';
import { any } from 'helpers/conditionals';
import SecurePaymentLock from '../../icons/SecurePaymentLock.svg';
import { analyticsWrapper } from 'analytics/itly/analyticsWrapper';
import withCart from 'components/PageWrapper/withCart';
import { Cart__Patched } from './cart';
import SvgContainer from 'icons/SvgContainer';
import AfterpayButton from 'features/afterpay/components/AfterpayButton';
import { TrackingHooksContainer } from 'components/CheckoutTrackingHooksContainer';
import itly from 'itly';
import { useTrackPageLoadTime } from 'analytics/itly/hooks/useTrackPageLoadTime';
import { ComponentName } from 'constants/componentName';
import { getLoadingComponentNames } from 'helpers/analytics';
import { applyShippingMethod, fetchCustomerPaymentMethods, getShippingRates, placeOrder } from 'store/cart/services';

interface CheckoutPageProps {
  cart: Cart__Patched;
  tracking: TrackingProp;
}

const StyledCartItemWrap = styled.div`
  width: 100%;
  padding-left: 1rem;
  padding-right: 1rem;
  margin: 1rem auto 0 auto;

  ${mediaQuery.medium`
    padding-right: 20%;
    margin: 1rem auto 6rem auto;
    max-width: 1180px;
  `}
`;

const PurchaseWarningMessage = styled(ErrorLabel)`
  font-size: 14px;
  letter-spacing: 0;
  text-transform: none;
  color: #ff0000;
`;

const SecurePurchaseText = styled.span`
  font-weight: 600;
  font-size: 12px;
  line-height: 14px;
  color: #757575;
  letter-spacing: 2px;
  text-transform: uppercase;
  padding-left: 0.5em;
  margin-bottom: 1em;
`;

export const CheckoutPage = ({ cart, tracking }: CheckoutPageProps): JSX.Element => {
  const dispatch = useDispatch();
  const email = useSelector(customerEmailSelector);
  const isVirtualCart = useSelector(isVirtualCartSelector);
  const windowSize = useWindowSize();
  const totalSegments = useSelector(totalSegmentsSelector);
  const requiresPayment = getGrandTotalFromTotalSegments(totalSegments) !== 0;
  const cartPaymentMethods = useSelector(cartPaymentMethodsSelector);
  const enablePaypalCheckout = findCustomerPaymentMethodInCartPaymentMethods(cartPaymentMethods).type === paypal;
  const hasBraintreePayment = _hasBraintreePayment(cartPaymentMethods);
  const shippingAddr = useSelector(shippingAddressSelector);
  const shippingMethod = useSelector(shippingMethodSelector);
  const shippingRates = useSelector(shippingRatesSelector);
  const cartLoadingState = useSelector(cartLoadingStateSelector);
  const storeCreditLoadingState = useSelector(storeCreditLoadingStateSelector);
  const customerLoadingState = useSelector(customerLoadingStateSelector);
  const cartShippingLoading = useSelector(shippingAddressLoadingStateSelector);
  const placeOrderLoading = useSelector(placeOrderLoadingStateSelector);
  const customerAddressesLoading = useSelector(customerShippingAddressLoadingStateSelector);
  const isAfterpayAppliedToCart = useSelector(isAfterpayAppliedToCartSelector);
  const isPaypalEnabled = useSelector(isPaypalEnabledSelector);
  const customer = useSelector(customerSelector);

  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const hasSufficientPayment = !requiresPayment || (cartPaymentMethods && isNotEmpty(cartPaymentMethods));
  const hasSufficientShipping = isVirtualCart || (!!shippingAddr.firstname && !!shippingMethod && shippingRates.length);

  const isFinishedLoading =
    isSuccess(cartLoadingState) &&
    (isVirtualCart || !isFetching(cartShippingLoading)) &&
    !isFetching(storeCreditLoadingState);

  const arePurchaseReqsMet = hasSufficientShipping && hasSufficientPayment && isFinishedLoading;

  const componentsToTrack = [ComponentName.SUMMARY, ComponentName.PAYMENT_INFORMATION];
  // TODO: Refactor these dependencies
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadingComponentNames = useMemo(() => getLoadingComponentNames(isVirtualCart, componentsToTrack, true), [
    isVirtualCart
  ]);

  useTrackPageLoadTime({
    componentNames: loadingComponentNames,
    eventType: 'checkoutPageTotalLoadTime',
    cartId: cart.entity_id,
    tracking,
    email
  });

  const updateShippingMethod = (carrier_code, method_code) => {
    const rate: Partial<QuoteDataShippingMethod> = {
      carrier_code,
      method_code
    };

    applyShippingMethod(dispatch, shippingAddr, rate).catch(console.error);
  };

  const fetchedMethods = useRef(false);

  useEffect(() => {
    if (!fetchedMethods.current && isFinishedLoading) {
      fetchedMethods.current = true;

      const fetchData = async () => {
        await fetchCustomerPaymentMethods(dispatch, totalSegments, isPaypalEnabled, isAfterpayAppliedToCart);
        await getShippingRates(dispatch, cart);
      };

      fetchData().catch(console.error);
    }
  }, [dispatch, cart, totalSegments, isPaypalEnabled, isAfterpayAppliedToCart, isFinishedLoading]);

  const handlePurchaseClick = useCallback(async () => {
    if (isPlacingOrder) {
      return;
    } // prevent rapid repeat clicks
    setIsPlacingOrder(true);

    const mainPaymentMethod = findCustomerPaymentMethodInCartPaymentMethods(
      cartPaymentMethods
    ) as CustomerPaymentMethod;
    const hasGiftCard = cartPaymentMethods.find(method => method.type === PAYMENT_METHOD_GIFTCARD);
    const methods = requiresPayment ? [mainPaymentMethod?.type] : [];
    if (hasGiftCard && !methods.includes(PAYMENT_METHOD_GIFTCARD)) {
      methods.push(PAYMENT_METHOD_GIFTCARD);
    }

    itly.paymentMethodAtCheckout({ payment_method: methods.join(',') });

    const request = requiresPayment ? buildPlaceOrderRequestPayload(mainPaymentMethod, '', null) : {
      paymentMethod: {
        method: 'braintree',
        additional_data: {
          payment_method_token: ''
        }
      }
    };
    try {
      await placeOrder(dispatch, mainPaymentMethod, request, cart, totalSegments, customer);
    } catch (error) {
      console.error('Error placing order:', error);
    } finally {
      setIsPlacingOrder(false);
    }
  }, [isPlacingOrder, cartPaymentMethods, dispatch, requiresPayment, cart, customer, totalSegments]);

  let cta = null;

  if (requiresPayment && isAfterpayAppliedToCart) {
    cta = <AfterpayButton disabled={!arePurchaseReqsMet} />;
  } else if (requiresPayment && enablePaypalCheckout) {
    cta = <PaypalButton disabled={!arePurchaseReqsMet} />;
  } else {
    cta = (
      <PrimaryLoadingButton
        isLoading={isPlacingOrder || isFetching(placeOrderLoading)}
        disabled={!arePurchaseReqsMet || isPlacingOrder}
        width="100%"
        onClick={handlePurchaseClick}
      >
        Purchase
      </PrimaryLoadingButton>
    );
  }

  return (
    <>
      {isFinishedLoading && <TrackingHooksContainer tracking={tracking} entityId={cart.entity_id} cart={cart} />}
      <CheckoutLayout
        cartId={cart.entity_id}
        tracking={tracking}
        activeStep="review"
        isFinalStep={true}
        ctaButton={
          <>
            <Subtext>
              By clicking "Purchase", you verify that you are using your own image or images that you have a copyright
              release to print. You also agree to the <a href="/terms-conditions">terms and conditions</a>.
            </Subtext>

            {cta}

            <Show when={requiresPayment && !hasBraintreePayment && !isAfterpayAppliedToCart}>
              <PurchaseWarningMessage className="secondary">Please provide a payment method.</PurchaseWarningMessage>
            </Show>
          </>
        }
      >
        <SEO title="Shopping Cart" />
        <PageHeader secondary>
          Review & Purchase
          <br />
          {SvgContainer(SecurePaymentLock)}
          <SecurePurchaseText>Secure Payments</SecurePurchaseText>
        </PageHeader>
        <CheckoutReviewTiles
          tracking={tracking}
          cartId={cart.entity_id}
          shippingLoading={any(
            isFetching(customerAddressesLoading),
            isFetching(customerLoadingState),
            !shippingAddr.firstname
          )}
          paymentLoading={isFetching(cartLoadingState)}
          shippingAddr={shippingAddr}
          cartPaymentMethods={cartPaymentMethods}
          openShippingAddressSelectionModal={() => dispatch(openShippingAddressSelectionModal())}
          openPaymentMethodSelectionModal={() => dispatch(openPaymentMethodSelectionModal())}
        />
        <Show when={isVirtualCart}>
          <CartItemList removeable={false} />
        </Show>
        <ShippingMethodOptions
          tracking={tracking}
          cart={cart}
          onChange={e => {
            const [carrier_code, method_code] = (e.target as any).value.split('_');
            updateShippingMethod(carrier_code, method_code);
          }}
          updateShippingMethod={updateShippingMethod}
        />
        <Show when={windowSize.width < 768 && !isVirtualCart}>
          <StyledCartItemWrap>
            <StyledH3 secondary>In Your Cart</StyledH3>
            <CartItemList removeable={false} />
          </StyledCartItemWrap>
        </Show>
      </CheckoutLayout>
      <Show when={windowSize.width >= 768 && !isVirtualCart}>
        <StyledCartItemWrap>
          <StyledH3 secondary>In Your Cart</StyledH3>
          <CartItemList removeable={false} />
        </StyledCartItemWrap>
      </Show>
    </>
  );
};

export default analyticsWrapper(withCart(CheckoutPage));
